// Fiche d'un marché (écran S12) : en-tête avec l'occupation du jour, filtres par situation et par
// zone, plan des emplacements en SVG (sélection au clic et au clavier), liste des emplacements
// pour les marchés sans plan, colonne de l'emplacement sélectionné, édition du plan par le
// responsable des marchés, liens vers les attributions.
import { Bouton, EtatVide, Lien, LienBouton, Panneau, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, PencilRuler } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useMarche } from './api';
import { EditeurPlan } from './EditeurPlan';
import { aujourdhuiIso, compterSituations, correspondFiltre, FILTRES_EMPLACEMENTS, LIBELLES_PERIODICITE, LIBELLES_SITUATION_EMPLACEMENT, resumeOccupation, situationEmplacement, type FiltreEmplacements } from './logique';
import { PanneauEmplacement } from './PanneauEmplacement';
import { PlanMarche } from './PlanMarche';
import { PlanGeographique } from './PlanGeographique';
import type { Emplacement, MarcheDetail, PeriodiciteRedevance, PlanMarche as Plan } from './types';
import styles from './marches.module.css';

function planPresent(m: MarcheDetail): Plan | null {
  const p = m.plan as Partial<Plan>;
  return typeof p.largeur === 'number' && typeof p.hauteur === 'number' ? (m.plan as Plan) : null;
}

function TableauEmplacements({ emplacements, selection, onSelection, aujourdhui }: { emplacements: Emplacement[]; selection: string | null; onSelection: (id: string) => void; aujourdhui: string }) {
  const colonnes: ColonneTableau<Emplacement>[] = [
    { id: 'reference', entete: 'Référence', enTeteLigne: true, masquable: false, valeurTri: (e) => e.reference, cellule: (e) => <Lien href={`/marches/emplacements/${e.id}`} className="cc-mono">{e.reference}</Lien> },
    { id: 'zone', entete: 'Zone', cellule: (e) => e.zone.id ? `${e.zone.code ?? ''}${e.zone.nom ? ` · ${e.zone.nom}` : ''}` : 'Sans zone', valeurTri: (e) => e.zone.code },
    { id: 'type', entete: 'Type', cellule: (e) => e.type_libelle },
    { id: 'surface', entete: 'Surface', numerique: true, cellule: (e) => `${e.surface_m2.toLocaleString('fr-FR')} m²`, valeurTri: (e) => e.surface_m2 },
    {
      id: 'situation',
      entete: 'Situation',
      cellule: (e) => {
        const s = situationEmplacement(e, aujourdhui);
        const ton = s === 'attribue' ? 'ok' : s === 'expire_bientot' ? 'attente' : s === 'hors_service' ? 'retard' : 'neutre';
        return <PuceEtat ton={ton} libelle={LIBELLES_SITUATION_EMPLACEMENT[s]} />;
      },
      valeurTri: (e) => e.etat,
    },
    { id: 'occupant', entete: 'Occupant', cellule: (e) => e.attribution_en_cours?.commercant ?? 'aucun' },
    {
      id: 'choisir',
      entete: 'Sélection',
      masquable: false,
      cellule: (e) => (
        <Bouton petit variante="discret" aria-pressed={selection === e.id} onClick={() => onSelection(e.id)}>
          {selection === e.id ? 'Sélectionné' : 'Sélectionner'}
        </Bouton>
      ),
    },
  ];
  return <Tableau legende="Emplacements" colonnes={colonnes} lignes={emplacements} cleLigne={(e) => e.id} cleColonnes="emplacements-marche" />;
}

export function PageMarche() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const [params, setParams] = useSearchParams();
  const marche = useMarche(id);
  const m = marche.data;
  const aujourdhui = aujourdhuiIso();
  const filtre = (params.get('filtre') as FiltreEmplacements | null) ?? 'tous';
  const zone = params.get('zone');
  const selection = params.get('emplacement');
  const [edition, setEdition] = useState(false);
  const [vueListe, setVueListe] = useState(false);
  const poser = (cle: string, valeur: string | null) =>
    setParams(
      (p) => {
        if (valeur === null || valeur === '' || valeur === 'tous') p.delete(cle);
        else p.set(cle, valeur);
        return p;
      },
      { replace: true },
    );
  const compte = useMemo(() => compterSituations(m?.emplacements ?? [], aujourdhui), [m, aujourdhui]);
  const plan = m ? planPresent(m) : null;
  const choisi = m?.emplacements.find((e) => e.id === selection) ?? null;
  const filtres = useMemo(() => (m?.emplacements ?? []).filter((e) => correspondFiltre(e, filtre, aujourdhui) && (!zone || e.zone.code === zone)), [m, filtre, zone, aujourdhui]);
  const peutModifier = peut(contexte, 'marche_municipal', 'modifier');

  return (
    <>
      <EnTetePage
        etiquette={m ? `Marché ${m.code}${m.bien.reference_inventaire ? ` · bien ${m.bien.reference_inventaire}` : ''} · ${m.zones.length} zone${m.zones.length > 1 ? 's' : ''} · ${m.emplacements.length} emplacement${m.emplacements.length > 1 ? 's' : ''}` : 'Marché'}
        titre={m?.nom ?? 'Marché'}
        description={
          m ? (
            <span>
              {resumeOccupation(m.statistiques, compte)} Redevances {LIBELLES_PERIODICITE[m.periodicite_defaut as PeriodiciteRedevance]?.toLowerCase() ?? m.periodicite_defaut} par défaut.
              {m.adresse ? ` ${m.adresse}.` : ''}
              {!m.actif ? ' Marché fermé : aucune attribution possible.' : ''}
            </span>
          ) : null
        }
        actions={
          <>
            <LienBouton href="/marches" icone={ArrowLeft}>
              Marchés
            </LienBouton>
            <LienBouton href={`/marches/attributions?marche=${id}`} variante="secondaire">
              Attributions du marché
            </LienBouton>
            {m && peutModifier ? (
              <Bouton variante="secondaire" icone={PencilRuler} aria-pressed={edition} onClick={() => setEdition((e) => !e)}>
                {edition ? 'Fermer l\'éditeur du plan' : 'Modifier le plan'}
              </Bouton>
            ) : null}
          </>
        }
      />
      <EtatRequete chargement={marche.isPending} erreur={marche.error} onReessayer={() => void marche.refetch()} lot="L2-B" fonction="La fiche du marché">
        {m ? (
          <div className={styles.disposition}>
            <div className={styles.pile}>
              {edition && m.mode !== 'ciel_ouvert' ? <EditeurPlan marche={m} onFermer={() => setEdition(false)} /> : null}
              <div className={styles.barreFiltres}>
                <div role="group" aria-label="Filtrer les emplacements" className={styles.puces}>
                  {FILTRES_EMPLACEMENTS.map((f) => (
                    <button key={f.valeur} type="button" className={styles.puce} aria-pressed={filtre === f.valeur} onClick={() => poser('filtre', f.valeur)}>
                      {f.libelle} <span className={styles.compteur}>{compte[f.valeur]}</span>
                    </button>
                  ))}
                </div>
                {m.zones.length > 0 ? (
                  <div role="group" aria-label="Zones" className={styles.puces}>
                    {m.zones.map((z) => (
                      <button key={z.id} type="button" className={styles.puce} aria-pressed={zone === z.code} onClick={() => poser('zone', zone === z.code ? null : z.code)}>
                        Zone {z.code}
                      </button>
                    ))}
                  </div>
                ) : null}
                {plan ? (
                  <Bouton petit variante="discret" aria-pressed={vueListe} onClick={() => setVueListe((v) => !v)}>
                    {vueListe ? 'Voir le plan' : 'Voir la liste'}
                  </Bouton>
                ) : null}
              </div>
              <Panneau titre="Plan des emplacements" niveau={2} id="plan">
                {m.mode === 'ciel_ouvert' ? <>
                  <PlanGeographique key={m.id} marche={m} edition={edition} selection={choisi} emplacements={filtres} onSelection={(e) => poser('emplacement', e)} />
                  <TableauEmplacements emplacements={filtres} selection={selection} onSelection={(e) => poser('emplacement', e)} aujourdhui={aujourdhui} />
                </> : plan && !vueListe ? (
                  <PlanMarche plan={plan} emplacements={m.emplacements} zones={m.zones} selection={selection} onSelection={(e) => poser('emplacement', e)} filtre={filtre} zoneActive={zone} aujourdhui={aujourdhui} />
                ) : m.emplacements.length === 0 ? (
                  <EtatVide titre="Aucun emplacement enregistré">
                    <p>Les zones et les emplacements de ce marché restent à créer par l&apos;API ou par l&apos;éditeur du plan.</p>
                  </EtatVide>
                ) : (
                  <>
                    {!plan ? <p className={styles.aide}>Aucun plan n&apos;est enregistré pour ce marché : les emplacements sont listés. Le responsable des marchés peut dessiner le plan avec l&apos;éditeur.</p> : null}
                    <TableauEmplacements emplacements={filtres} selection={selection} onSelection={(e) => poser('emplacement', e)} aujourdhui={aujourdhui} />
                  </>
                )}
              </Panneau>
            </div>
            <aside className={styles.cote} aria-label="Emplacement sélectionné">
              <PanneauEmplacement emplacement={choisi} aujourdhui={aujourdhui} />
            </aside>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
